import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import AgentService from "@/services/ai/agent";
import type { AgentConversationSummary } from "@/dtos/Agent.dto";

export const AGENT_CONVERSATIONS_KEY = ["agent", "conversations"] as const;

/** The rep's saved agent threads (most recent first), renaming and deleting one. */
export function useAgentConversations() {
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: AGENT_CONVERSATIONS_KEY,
    queryFn: async (): Promise<AgentConversationSummary[]> => {
      const result = await AgentService.listConversations();
      if (!result.data.status) throw new Error(result.data.msg);
      return (result.data.data ?? []) as AgentConversationSummary[];
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string): Promise<string> => {
      const result = await AgentService.deleteConversation(id);
      if (!result.data.status) throw new Error(result.data.msg);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENT_CONVERSATIONS_KEY });
    },
  });

  const rename = useMutation({
    mutationFn: async ({ id, title }: { id: string; title: string }) => {
      const result = await AgentService.renameConversation(id, title);
      if (!result.data.status) throw new Error(result.data.msg);
      return { id, title };
    },
    // Show the new name straight away; put the old one back if the save fails.
    onMutate: async ({ id, title }) => {
      await queryClient.cancelQueries({ queryKey: AGENT_CONVERSATIONS_KEY });
      const previous =
        queryClient.getQueryData<AgentConversationSummary[]>(AGENT_CONVERSATIONS_KEY);
      queryClient.setQueryData<AgentConversationSummary[]>(AGENT_CONVERSATIONS_KEY, (old) =>
        old?.map((conversation) =>
          conversation.id === id ? { ...conversation, title } : conversation
        )
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(AGENT_CONVERSATIONS_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: AGENT_CONVERSATIONS_KEY });
    },
  });

  return { list, remove, rename };
}
